import { useState, useEffect } from 'react';
import { api } from '../lib/api';

interface Endpoint {
  id: string;
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
  title: string;
  description: string;
  auth: string;
  category: string;
  requestBody?: any;
  requestParams?: Array<{ name: string; type: string; required: boolean; description: string; default?: any }>;
  queryParams?: Array<{ name: string; type: string; required: boolean; description: string; default?: any }>;
  responseExample: any;
  errorExample?: any;
  cosmosSchema?: {
    required: string[];
    optional: string[];
    defaults: Record<string, any>;
    notes?: string[];
  };
  requestNotes?: string[];
  useCases?: string[];
  curlExample: string;
}

interface Category {
  id: string;
  name: string;
  endpoints: Endpoint[];
}

const METHOD_COLORS = {
  GET: 'bg-blue-500',
  POST: 'bg-green-500',
  PUT: 'bg-yellow-500',
  PATCH: 'bg-orange-500',
  DELETE: 'bg-red-500',
};

export function ApiHelpPage() {
  const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedEndpoint, setSelectedEndpoint] = useState<Endpoint | null>(null);
  const [activeTab, setActiveTab] = useState<'request' | 'response' | 'test'>('request');
  const [loading, setLoading] = useState(true);
  
  // Test tab state
  const [testRequestBody, setTestRequestBody] = useState('{}');
  const [testQueryParams, setTestQueryParams] = useState<Record<string, string>>({});
  const [testPathParams, setTestPathParams] = useState<Record<string, string>>({});
  const [testResponse, setTestResponse] = useState<any>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testDuration, setTestDuration] = useState<number | null>(null);
  const [testRequestDetails, setTestRequestDetails] = useState<{
    method: string;
    url: string;
    headers: Record<string, string>;
    body?: string;
  } | null>(null);
  
  // Lifecycle controls for fetch API
  const [enableMarkServed, setEnableMarkServed] = useState(false);
  const [enableGeneration, setEnableGeneration] = useState(false);
  const [enableUpdateServedAt, setEnableUpdateServedAt] = useState(false);
  
  // History mode controls for fetch API
  const [enableHistory, setEnableHistory] = useState(false);
  const [historyOffset, setHistoryOffset] = useState(0);
  
  // AllUsers mode controls for fetch API
  const [enableAllUsers, setEnableAllUsers] = useState(false);
  
  // Error simulation for fetch API
  const [simulateError, setSimulateError] = useState('');

  useEffect(() => {
    loadEndpoints();
  }, []);

  // Initialize test parameters when endpoint changes
  useEffect(() => {
    if (selectedEndpoint) {
      // Initialize request body
      if (selectedEndpoint.requestBody) {
        setTestRequestBody(JSON.stringify(selectedEndpoint.requestBody, null, 2));
      } else {
        setTestRequestBody('{}');
      }
      
      // Initialize path params
      const pathParams: Record<string, string> = {};
      selectedEndpoint.requestParams?.forEach(param => {
        pathParams[param.name] = param.default || '';
      });
      setTestPathParams(pathParams);
      
      // Initialize query params
      const queryParams: Record<string, string> = {};
      selectedEndpoint.queryParams?.forEach(param => {
        queryParams[param.name] = param.default || '';
      });
      setTestQueryParams(queryParams);
      
      // Reset response
      setTestResponse(null);
      setTestError(null);
      setTestDuration(null);
    }
  }, [selectedEndpoint]);

  const loadEndpoints = async () => {
    try {
      const response = await api.getAllEndpoints() as { 
        endpoints: Endpoint[]; 
        categories: Category[];
        success: boolean;
        total: number;
      };
      setEndpoints(response.endpoints);
      setCategories(response.categories);
      
      // Select first endpoint by default
      if (response.endpoints.length > 0) {
        setSelectedEndpoint(response.endpoints[0]);
      }
    } catch (err) {
      console.error('Failed to load endpoints:', err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const sendTestRequest = async () => {
    if (!selectedEndpoint) return;
    
    setTestLoading(true);
    setTestError(null);
    setTestResponse(null);
    setTestDuration(null);
    setTestRequestDetails(null);
    
    const startTime = performance.now();
    
    try {
      // Build URL with path params
      let url = selectedEndpoint.path;
      Object.entries(testPathParams).forEach(([key, value]) => {
        url = url.replace(`:${key}`, encodeURIComponent(value));
      });
      
      // Add query params
      const queryParamsToSend = { ...testQueryParams };
      
      // Add lifecycle control flags for fetch API
      if (selectedEndpoint.id === 'content-fetch') {
        queryParamsToSend['_markServed'] = enableMarkServed.toString();
        queryParamsToSend['_triggerGeneration'] = enableGeneration.toString();
        queryParamsToSend['updateServedAt'] = enableUpdateServedAt.toString();
        
        // Add history mode flags
        if (enableHistory) {
          queryParamsToSend['includeServed'] = 'true';
          queryParamsToSend['offset'] = historyOffset.toString();
        }
        
        // Add allUsers mode flag
        if (enableAllUsers) {
          queryParamsToSend['allUsers'] = 'true';
        }
        
        // Add error simulation
        if (simulateError) {
          queryParamsToSend['simulateError'] = simulateError;
        }
      }
      
      const queryString = Object.entries(queryParamsToSend)
        .filter(([_, value]) => value !== '')
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&');
      if (queryString) {
        url += `?${queryString}`;
      }
      
      // Prepare request options
      const options: RequestInit = {
        method: selectedEndpoint.method,
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': api['apiKey'] || '', // Access private apiKey property
        },
      };
      
      // Add body for POST/PUT/PATCH
      let requestBodyString: string | undefined;
      if (['POST', 'PUT', 'PATCH'].includes(selectedEndpoint.method)) {
        try {
          const body = JSON.parse(testRequestBody);
          requestBodyString = JSON.stringify(body, null, 2);
          options.body = JSON.stringify(body);
        } catch (err) {
          throw new Error('Invalid JSON in request body');
        }
      }
      
      // Store request details for display
      const baseUrl = api['baseUrl'] || window.location.origin;
      setTestRequestDetails({
        method: selectedEndpoint.method,
        url: `${baseUrl}${url}`,
        headers: options.headers as Record<string, string>,
        body: requestBodyString
      });
      
      // Make request
      const response = await fetch(`${baseUrl}${url}`, options);
      
      const responseData = await response.json();
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      setTestDuration(duration);
      
      if (!response.ok) {
        setTestError(`HTTP ${response.status}: ${responseData.error || response.statusText}`);
        setTestResponse(responseData);
      } else {
        setTestResponse(responseData);
      }
    } catch (err: any) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      setTestDuration(duration);
      setTestError(err.message || 'Failed to send request');
    } finally {
      setTestLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-linepe-orange"></div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-120px)]">
      {/* Sidebar */}
      <div className="w-80 bg-white border-r border-gray-200 overflow-y-auto">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <h2 className="font-bold text-gray-900">API Reference</h2>
          <p className="text-sm text-gray-600 mt-1">{endpoints.length} endpoints</p>
        </div>

        {categories.map((category) => (
          <div key={category.id} className="border-b border-gray-100">
            <div className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-700 uppercase tracking-wide">
              {category.name}
            </div>
            {category.endpoints.map((endpoint) => (
              <button
                key={endpoint.id}
                onClick={() => {
                  setSelectedEndpoint(endpoint);
                  setActiveTab('request');
                }}
                className={`w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors border-l-2 ${
                  selectedEndpoint?.id === endpoint.id
                    ? 'border-linepe-orange bg-orange-50'
                    : 'border-transparent'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`${METHOD_COLORS[endpoint.method]} text-white text-xs font-bold px-2 py-0.5 rounded`}>
                    {endpoint.method}
                  </span>
                  <span className="text-sm font-medium text-gray-900">{endpoint.title}</span>
                </div>
                <code className="text-xs text-gray-600">{endpoint.path}</code>
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        {selectedEndpoint ? (
          <div className="max-w-5xl mx-auto p-8">
            {/* Header */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`${METHOD_COLORS[selectedEndpoint.method]} text-white text-sm font-bold px-3 py-1 rounded`}>
                      {selectedEndpoint.method}
                    </span>
                    <h1 className="text-2xl font-bold text-gray-900">{selectedEndpoint.title}</h1>
                  </div>
                  <code className="text-sm bg-gray-100 px-3 py-1 rounded text-gray-800">
                    {selectedEndpoint.path}
                  </code>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500 mb-1">Authentication</div>
                  <div className="text-sm font-semibold text-gray-900">{selectedEndpoint.auth}</div>
                </div>
              </div>
              <p className="text-gray-600">{selectedEndpoint.description}</p>
              
              {/* Use Cases */}
              {selectedEndpoint.useCases && selectedEndpoint.useCases.length > 0 && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <div className="text-xs font-semibold text-blue-900 mb-2">💡 Use Cases</div>
                  <ul className="space-y-1">
                    {selectedEndpoint.useCases.map((useCase, idx) => (
                      <li key={idx} className="text-sm text-blue-800 flex items-start gap-2">
                        <span className="text-blue-600">•</span>
                        <span>{useCase}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="bg-white rounded-lg shadow-sm mb-6">
              <div className="border-b border-gray-200">
                <div className="flex">
                  <button
                    onClick={() => setActiveTab('request')}
                    className={`px-6 py-3 font-semibold transition-colors ${
                      activeTab === 'request'
                        ? 'text-linepe-orange border-b-2 border-linepe-orange'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Request
                  </button>
                  <button
                    onClick={() => setActiveTab('response')}
                    className={`px-6 py-3 font-semibold transition-colors ${
                      activeTab === 'response'
                        ? 'text-linepe-orange border-b-2 border-linepe-orange'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Response
                  </button>
                  <button
                    onClick={() => setActiveTab('test')}
                    className={`px-6 py-3 font-semibold transition-colors ${
                      activeTab === 'test'
                        ? 'text-linepe-orange border-b-2 border-linepe-orange'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Test
                  </button>
                </div>
              </div>

              <div className="p-6">
                {activeTab === 'request' ? (
                  <div className="space-y-6">
                    {/* Request Notes */}
                    {selectedEndpoint.requestNotes && selectedEndpoint.requestNotes.length > 0 && (
                      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                        <div className="text-sm font-semibold text-yellow-900 mb-2">📝 Important Notes</div>
                        <ul className="space-y-1.5">
                          {selectedEndpoint.requestNotes.map((note, idx) => (
                            <li key={idx} className="text-sm text-yellow-800">{note}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Path Parameters */}
                    {selectedEndpoint.requestParams && selectedEndpoint.requestParams.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Path Parameters</h3>
                        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                          {selectedEndpoint.requestParams.map((param) => (
                            <div key={param.name} className="flex items-start gap-4">
                              <code className="text-sm font-mono text-blue-600">{param.name}</code>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">{param.type}</span>
                                  {param.required && (
                                    <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">required</span>
                                  )}
                                  {param.default && (
                                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                      default: {typeof param.default === 'object' ? JSON.stringify(param.default) : param.default}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600">{param.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Query Parameters */}
                    {selectedEndpoint.queryParams && selectedEndpoint.queryParams.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Query Parameters</h3>
                        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                          {selectedEndpoint.queryParams.map((param) => (
                            <div key={param.name} className="flex items-start gap-4">
                              <code className="text-sm font-mono text-blue-600">{param.name}</code>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="text-xs bg-gray-200 px-2 py-0.5 rounded">{param.type}</span>
                                  {param.required && (
                                    <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">required</span>
                                  )}
                                  {param.default && (
                                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                      default: {typeof param.default === 'object' ? JSON.stringify(param.default) : param.default}
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600">{param.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Request Body */}
                    {selectedEndpoint.requestBody && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Request Body</h3>
                        <div className="relative">
                          <button
                            onClick={() => copyToClipboard(JSON.stringify(selectedEndpoint.requestBody, null, 2))}
                            className="absolute top-3 right-3 text-xs bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded transition-colors"
                          >
                            Copy
                          </button>
                          <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                            {JSON.stringify(selectedEndpoint.requestBody, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}

                    {/* Cosmos DB Schema */}
                    {selectedEndpoint.cosmosSchema && (
                      <div className="border border-purple-200 rounded-lg bg-purple-50 p-4">
                        <h3 className="text-sm font-semibold text-purple-900 mb-3 flex items-center gap-2">
                          <span>🗄️</span>
                          Cosmos DB Schema
                        </h3>
                        
                        {/* Required Fields */}
                        <div className="mb-4">
                          <div className="text-xs font-semibold text-purple-800 mb-2">Required Fields</div>
                          <div className="flex flex-wrap gap-2">
                            {selectedEndpoint.cosmosSchema.required.map((field) => (
                              <code key={field} className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded font-mono">
                                {field}
                              </code>
                            ))}
                          </div>
                        </div>
                        
                        {/* Optional Fields */}
                        <div className="mb-4">
                          <div className="text-xs font-semibold text-purple-800 mb-2">Optional Fields</div>
                          <div className="flex flex-wrap gap-2">
                            {selectedEndpoint.cosmosSchema.optional.map((field) => (
                              <code key={field} className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded font-mono">
                                {field}
                              </code>
                            ))}
                          </div>
                        </div>
                        
                        {/* Defaults */}
                        {Object.keys(selectedEndpoint.cosmosSchema.defaults).length > 0 && (
                          <div className="mb-3">
                            <div className="text-xs font-semibold text-purple-800 mb-2">Default Values</div>
                            <div className="bg-white rounded p-2">
                              <pre className="text-xs text-gray-800 font-mono">
                                {JSON.stringify(selectedEndpoint.cosmosSchema.defaults, null, 2)}
                              </pre>
                            </div>
                          </div>
                        )}
                        
                        {/* Schema Notes */}
                        {selectedEndpoint.cosmosSchema.notes && selectedEndpoint.cosmosSchema.notes.length > 0 && (
                          <div>
                            <div className="text-xs font-semibold text-purple-800 mb-2">Notes</div>
                            <ul className="space-y-1">
                              {selectedEndpoint.cosmosSchema.notes.map((note, idx) => (
                                <li key={idx} className="text-xs text-purple-700 flex items-start gap-1.5">
                                  <span>•</span>
                                  <span>{note}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {/* cURL Example */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">cURL Example</h3>
                      <div className="relative">
                        <button
                          onClick={() => copyToClipboard(selectedEndpoint.curlExample)}
                          className="absolute top-3 right-3 text-xs bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded transition-colors z-10"
                        >
                          Copy
                        </button>
                        <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                          {selectedEndpoint.curlExample}
                        </pre>
                      </div>
                    </div>
                  </div>
                ) : activeTab === 'response' ? (
                  <div className="space-y-6">
                    {/* Success Response */}
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Success Response (200)</h3>
                      <div className="relative">
                        <button
                          onClick={() => copyToClipboard(JSON.stringify(selectedEndpoint.responseExample, null, 2))}
                          className="absolute top-3 right-3 text-xs bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded transition-colors"
                        >
                          Copy
                        </button>
                        <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                          {JSON.stringify(selectedEndpoint.responseExample, null, 2)}
                        </pre>
                      </div>
                    </div>

                    {/* Error Response */}
                    {selectedEndpoint.errorExample && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Error Response (4xx/5xx)</h3>
                        <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                          {JSON.stringify(selectedEndpoint.errorExample, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Test Tab Content */}
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
                      <div className="flex items-start gap-2">
                        <span className="text-blue-700">ℹ️</span>
                        <div className="text-sm text-blue-800">
                          <p className="font-semibold mb-1">Live API Testing</p>
                          <p>Modify the parameters below and send a real request to the API endpoint. The request will use your configured API key.</p>
                        </div>
                      </div>
                    </div>

                    {/* Path Parameters */}
                    {selectedEndpoint.requestParams && selectedEndpoint.requestParams.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Path Parameters</h3>
                        <div className="space-y-3">
                          {selectedEndpoint.requestParams.map((param) => (
                            <div key={param.name} className="flex flex-col gap-2">
                              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                                {param.name}
                                {param.required && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">required</span>}
                                <span className="text-xs text-gray-500">({param.type})</span>
                              </label>
                              <input
                                type="text"
                                value={testPathParams[param.name] || ''}
                                onChange={(e) => setTestPathParams({ ...testPathParams, [param.name]: e.target.value })}
                                placeholder={param.description}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linepe-orange focus:border-transparent"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Query Parameters */}
                    {selectedEndpoint.queryParams && selectedEndpoint.queryParams.length > 0 && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Query Parameters</h3>
                        <div className="space-y-3">
                          {selectedEndpoint.queryParams.map((param) => (
                            <div key={param.name} className="flex flex-col gap-2">
                              <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                                {param.name}
                                {param.required && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">required</span>}
                                <span className="text-xs text-gray-500">({param.type})</span>
                              </label>
                              <input
                                type="text"
                                value={testQueryParams[param.name] || ''}
                                onChange={(e) => setTestQueryParams({ ...testQueryParams, [param.name]: e.target.value })}
                                placeholder={param.description}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-linepe-orange focus:border-transparent"
                              />
                            </div>
                          ))}
                        </div>
                        
                        {/* Lifecycle Controls for Fetch API */}
                        {selectedEndpoint.id === 'content-fetch' && (
                          <div className="mt-6 border border-blue-200 rounded-lg bg-blue-50 p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <span className="text-blue-700">🔄</span>
                              <h4 className="text-sm font-semibold text-blue-900">Lifecycle Management Controls</h4>
                            </div>
                            <p className="text-xs text-blue-800 mb-4">
                              Control whether content should be marked as served and trigger new generation. 
                              For testing purposes, you may want to disable these to fetch the same content multiple times.
                            </p>
                            
                            <div className="space-y-3">
                              {/* Mark as Served Toggle */}
                              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-200">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <label htmlFor="enableMarkServed" className="text-sm font-medium text-gray-900 cursor-pointer">
                                      Mark Content as Served
                                    </label>
                                    {enableMarkServed && (
                                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">
                                        ACTIVE
                                      </span>
                                    )}
                                    {!enableMarkServed && (
                                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                        DISABLED
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-600 mt-1">
                                    When enabled, content will be marked as served (isServed = true) after fetch
                                  </p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer ml-3">
                                  <input
                                    id="enableMarkServed"
                                    type="checkbox"
                                    checked={enableMarkServed}
                                    onChange={(e) => setEnableMarkServed(e.target.checked)}
                                    className="sr-only peer"
                                  />
                                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                </label>
                              </div>
                              
                              {/* Trigger Generation Toggle */}
                              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-blue-200">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <label htmlFor="enableGeneration" className="text-sm font-medium text-gray-900 cursor-pointer">
                                      Trigger New Generation
                                    </label>
                                    {enableGeneration && (
                                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">
                                        ACTIVE
                                      </span>
                                    )}
                                    {!enableGeneration && (
                                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                        DISABLED
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-600 mt-1">
                                    When enabled, will trigger generation if inventory is low (keeps 2+ cards ready)
                                  </p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer ml-3">
                                  <input
                                    id="enableGeneration"
                                    type="checkbox"
                                    checked={enableGeneration}
                                    onChange={(e) => setEnableGeneration(e.target.checked)}
                                    className="sr-only peer"
                                  />
                                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                </label>
                              </div>
                              
                              {/* Update ServedAt Toggle */}
                              <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-200">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <label htmlFor="enableUpdateServedAt" className="text-sm font-medium text-gray-900 cursor-pointer">
                                      Update ServedAt Timestamp
                                    </label>
                                    {enableUpdateServedAt && (
                                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-medium">
                                        ACTIVE
                                      </span>
                                    )}
                                    {!enableUpdateServedAt && (
                                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                        DISABLED
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-600 mt-1">
                                    Updates servedAt timestamp to current time for all fetched items (for production tracking)
                                  </p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer ml-3">
                                  <input
                                    id="enableUpdateServedAt"
                                    type="checkbox"
                                    checked={enableUpdateServedAt}
                                    onChange={(e) => setEnableUpdateServedAt(e.target.checked)}
                                    className="sr-only peer"
                                  />
                                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                                </label>
                              </div>
                            </div>
                            
                            {!enableMarkServed && !enableGeneration && (
                              <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                                <p className="text-xs text-yellow-800">
                                  ⚠️ Both features are disabled. Content will be fetched without any lifecycle management (good for testing).
                                </p>
                              </div>
                            )}
                            
                            {/* History Mode Controls */}
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <h4 className="text-sm font-semibold text-gray-900 mb-3">📜 History Mode (View Served Content)</h4>
                              
                              <div className="space-y-3">
                                {/* History Mode Toggle */}
                                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-200">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <label htmlFor="enableHistory" className="text-sm font-medium text-gray-900 cursor-pointer">
                                        Enable History Mode
                                      </label>
                                      {enableHistory && (
                                        <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded font-medium">
                                          ACTIVE
                                        </span>
                                      )}
                                      {!enableHistory && (
                                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                          DISABLED
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-xs text-gray-600 mt-1">
                                      When enabled, returns served content (isServed=true) from ALL users (requires admin key)
                                    </p>
                                  </div>
                                  <label className="relative inline-flex items-center cursor-pointer ml-3">
                                    <input
                                      id="enableHistory"
                                      type="checkbox"
                                      checked={enableHistory}
                                      onChange={(e) => {
                                        setEnableHistory(e.target.checked);
                                        if (!e.target.checked) {
                                          setHistoryOffset(0);
                                        }
                                      }}
                                      className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                                  </label>
                                </div>
                                
                                {/* Offset Control (only visible when history mode is enabled) */}
                                {enableHistory && (
                                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                                    <label htmlFor="historyOffset" className="block text-sm font-medium text-gray-900 mb-2">
                                      Pagination Offset
                                    </label>
                                    <div className="flex items-center gap-3">
                                      <input
                                        id="historyOffset"
                                        type="number"
                                        min="0"
                                        step="10"
                                        value={historyOffset}
                                        onChange={(e) => setHistoryOffset(Math.max(0, parseInt(e.target.value) || 0))}
                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                      />
                                      <div className="flex gap-2">
                                        <button
                                          onClick={() => setHistoryOffset(Math.max(0, historyOffset - 10))}
                                          disabled={historyOffset === 0}
                                          className="px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                                        >
                                          ← Prev
                                        </button>
                                        <button
                                          onClick={() => setHistoryOffset(historyOffset + 10)}
                                          className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
                                        >
                                          Next →
                                        </button>
                                      </div>
                                    </div>
                                    <p className="text-xs text-gray-600 mt-2">
                                      Offset {historyOffset} - will skip first {historyOffset} items
                                    </p>
                                  </div>
                                )}
                                
                                {enableHistory && (
                                  <div className="p-2 bg-purple-50 border border-purple-200 rounded">
                                    <p className="text-xs text-purple-800">
                                      💡 <strong>Tip:</strong> Remove userId from query params to see ALL users' history, or keep userId to see specific user's history.
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* AllUsers Mode Controls */}
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <h4 className="text-sm font-semibold text-gray-900 mb-3">👥 AllUsers Mode (Cross-User Content)</h4>
                              
                              <div className="space-y-3">
                                {/* AllUsers Mode Toggle */}
                                <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-200">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <label htmlFor="enableAllUsers" className="text-sm font-medium text-gray-900 cursor-pointer">
                                        Enable AllUsers Mode
                                      </label>
                                      {enableAllUsers && (
                                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">
                                          ACTIVE
                                        </span>
                                      )}
                                      {!enableAllUsers && (
                                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                                          DISABLED
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-xs text-gray-600 mt-1">
                                      When enabled, returns unserved active content (isActive=true, isServed=false) from ALL users
                                    </p>
                                  </div>
                                  <label className="relative inline-flex items-center cursor-pointer ml-3">
                                    <input
                                      id="enableAllUsers"
                                      type="checkbox"
                                      checked={enableAllUsers}
                                      onChange={(e) => setEnableAllUsers(e.target.checked)}
                                      className="sr-only peer"
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                                  </label>
                                </div>
                                
                                {enableAllUsers && (
                                  <div className="p-2 bg-green-50 border border-green-200 rounded">
                                    <p className="text-xs text-green-800">
                                      💡 <strong>Tip:</strong> AllUsers mode works with any API key (mobile, admin, internal) and returns only active, unserved content across all users. Use with contentType filter for specific content types.
                                    </p>
                                  </div>
                                )}
                                
                                {enableAllUsers && enableHistory && (
                                  <div className="p-2 bg-yellow-50 border border-yellow-200 rounded">
                                    <p className="text-xs text-yellow-800">
                                      ⚠️ <strong>Warning:</strong> Both AllUsers and History modes are enabled. History mode will take precedence (returns served content).
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Error Simulation Controls */}
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <h4 className="text-sm font-semibold text-gray-900 mb-3">🧪 Error Simulation (Testing)</h4>
                              
                              <div className="space-y-3">
                                {/* Error Type Selector */}
                                <div className="p-3 bg-white rounded-lg border border-red-200">
                                  <label htmlFor="simulateError" className="block text-sm font-medium text-gray-900 mb-2">
                                    Select Error Type
                                  </label>
                                  <select
                                    id="simulateError"
                                    value={simulateError}
                                    onChange={(e) => setSimulateError(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
                                  >
                                    <option value="">None (Normal Response)</option>
                                    <option value="content_filter">Content Filter (400 - Not Retryable)</option>
                                    <option value="timeout">Timeout (504 - Retryable after 5s)</option>
                                    <option value="rate_limit">Rate Limit (429 - Retryable after 60s)</option>
                                    <option value="no_content">No Content (404 - Retryable after 300s)</option>
                                    <option value="database_error">Database Error (503 - Retryable after 10s)</option>
                                    <option value="invalid_user">Invalid User (403 - Not Retryable)</option>
                                    <option value="server_error">Server Error (500 - Retryable after 30s)</option>
                                    <option value="network_error">Network Error (Connection Closed)</option>
                                  </select>
                                  <p className="text-xs text-gray-600 mt-2">
                                    Simulates various error scenarios to test mobile app error handling
                                  </p>
                                </div>
                                
                                {simulateError && (
                                  <div className="p-3 bg-red-50 border border-red-200 rounded">
                                    <div className="flex items-start gap-2">
                                      <span className="text-red-600 text-xl">⚠️</span>
                                      <div className="flex-1">
                                        <p className="text-xs text-red-800 font-semibold mb-1">
                                          Error Simulation Active
                                        </p>
                                        <p className="text-xs text-red-700">
                                          This request will return an error response instead of actual content. Use this to test how your mobile app handles different error scenarios.
                                        </p>
                                        {simulateError === 'content_filter' && (
                                          <p className="text-xs text-red-600 mt-2 font-medium">
                                            → Content moderation blocked (not retryable, fallback available)
                                          </p>
                                        )}
                                        {simulateError === 'timeout' && (
                                          <p className="text-xs text-red-600 mt-2 font-medium">
                                            → API timeout after 30 seconds (retryable after 5s)
                                          </p>
                                        )}
                                        {simulateError === 'rate_limit' && (
                                          <p className="text-xs text-red-600 mt-2 font-medium">
                                            → Rate limit exceeded (retryable after 60s)
                                          </p>
                                        )}
                                        {simulateError === 'no_content' && (
                                          <p className="text-xs text-red-600 mt-2 font-medium">
                                            → No content available in inventory (retryable after 300s)
                                          </p>
                                        )}
                                        {simulateError === 'database_error' && (
                                          <p className="text-xs text-red-600 mt-2 font-medium">
                                            → Database connection failed (retryable after 10s)
                                          </p>
                                        )}
                                        {simulateError === 'invalid_user' && (
                                          <p className="text-xs text-red-600 mt-2 font-medium">
                                            → User not authorized (not retryable)
                                          </p>
                                        )}
                                        {simulateError === 'server_error' && (
                                          <p className="text-xs text-red-600 mt-2 font-medium">
                                            → Internal server error (retryable after 30s)
                                          </p>
                                        )}
                                        {simulateError === 'network_error' && (
                                          <p className="text-xs text-red-600 mt-2 font-medium">
                                            → Connection will be closed (network error)
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Special notice for leaderboard cross-partition queries */}
                        {selectedEndpoint.id === 'content-fetch' && testQueryParams['contentType'] === 'leaderboard' && (
                          <div className="mt-4 bg-purple-50 border-l-4 border-purple-500 p-4 rounded">
                            <div className="flex items-start gap-2">
                              <span className="text-purple-700 text-lg">🏆</span>
                              <div className="text-sm text-purple-800">
                                <p className="font-semibold mb-1">Cross-Partition Query Mode</p>
                                <p className="mb-2">
                                  When contentType is 'leaderboard', the API queries across ALL user partitions 
                                  to return the top N performers, not just content from the specified userId.
                                </p>
                                <ul className="space-y-1 ml-4 list-disc">
                                  <li>Returns documents with different userId values (actual top performers)</li>
                                  <li>Orders results by rank_number ASC (1, 2, 3, 4, 5...)</li>
                                  <li>Higher RU cost than single-partition queries</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Request Body (for POST/PUT/PATCH) */}
                    {['POST', 'PUT', 'PATCH'].includes(selectedEndpoint.method) && (
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900 mb-3">Request Body (JSON)</h3>
                        <textarea
                          value={testRequestBody}
                          onChange={(e) => setTestRequestBody(e.target.value)}
                          rows={12}
                          className="w-full px-3 py-2 font-mono text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-linepe-orange focus:border-transparent"
                          placeholder='{"key": "value"}'
                        />
                      </div>
                    )}

                    {/* Send Button */}
                    <div className="pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                          Endpoint: <code className="bg-gray-100 px-2 py-1 rounded">{selectedEndpoint.method} {selectedEndpoint.path}</code>
                        </div>
                        <button
                          onClick={sendTestRequest}
                          disabled={testLoading}
                          className="px-6 py-2 bg-linepe-orange text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {testLoading ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Sending...
                            </>
                          ) : (
                            <>
                              <span>▶</span>
                              Send Request
                            </>
                          )}
                        </button>
                      </div>
                      
                      {/* Request Duration */}
                      {testDuration !== null && (
                        <div className="mt-3 text-sm text-gray-600 flex items-center gap-2">
                          <span className="text-gray-500">⏱️</span>
                          <span>
                            Request completed in <span className="font-semibold text-gray-900">{testDuration.toFixed(0)}ms</span>
                            {testDuration >= 1000 && <span className="text-gray-500"> ({(testDuration / 1000).toFixed(2)}s)</span>}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Request Details */}
                    {testRequestDetails && (
                      <div className="border-t border-gray-200 pt-6 space-y-4">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <span className="text-blue-600">📤</span>
                            Request Details
                          </h3>
                          
                          {/* Request Line */}
                          <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-center gap-2 font-mono text-sm">
                              <span className={`${METHOD_COLORS[testRequestDetails.method as keyof typeof METHOD_COLORS]} text-white text-xs font-bold px-2 py-1 rounded`}>
                                {testRequestDetails.method}
                              </span>
                              <span className="text-blue-900 break-all">{testRequestDetails.url}</span>
                            </div>
                          </div>
                          
                          {/* Request Headers */}
                          <div className="mb-3">
                            <div className="text-xs font-semibold text-gray-700 mb-2">Headers:</div>
                            <div className="relative">
                              <button
                                onClick={() => copyToClipboard(JSON.stringify(testRequestDetails.headers, null, 2))}
                                className="absolute top-2 right-2 text-xs bg-gray-700 hover:bg-gray-600 text-white px-2 py-1 rounded transition-colors z-10"
                              >
                                Copy
                              </button>
                              <pre className="bg-gray-900 text-gray-100 p-3 rounded-lg overflow-x-auto text-xs">
                                {JSON.stringify(testRequestDetails.headers, null, 2)}
                              </pre>
                            </div>
                          </div>
                          
                          {/* Request Body */}
                          {testRequestDetails.body && (
                            <div>
                              <div className="text-xs font-semibold text-gray-700 mb-2">Body:</div>
                              <div className="relative">
                                <button
                                  onClick={() => copyToClipboard(testRequestDetails.body || '')}
                                  className="absolute top-2 right-2 text-xs bg-gray-700 hover:bg-gray-600 text-white px-2 py-1 rounded transition-colors z-10"
                                >
                                  Copy
                                </button>
                                <pre className="bg-gray-900 text-gray-100 p-3 rounded-lg overflow-x-auto text-xs">
                                  {testRequestDetails.body}
                                </pre>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Response */}
                    {(testResponse || testError) && (
                      <div className="border-t border-gray-200 pt-6 space-y-6">
                        <div>
                          <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            {testError ? (
                              <>
                                <span className="text-red-600">❌</span>
                                Response (Error)
                              </>
                            ) : (
                              <>
                                <span className="text-green-600">✅</span>
                                Response (Success)
                              </>
                            )}
                          </h3>
                          
                          {testError && (
                            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                              <p className="text-sm text-red-800 font-medium">{testError}</p>
                            </div>
                          )}
                          
                          {testResponse && (
                            <div className="relative">
                              <button
                                onClick={() => copyToClipboard(JSON.stringify(testResponse, null, 2))}
                                className="absolute top-3 right-3 text-xs bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded transition-colors z-10"
                              >
                                Copy
                              </button>
                              <pre className={`p-4 rounded-lg overflow-x-auto text-sm ${
                                testError ? 'bg-red-900 text-red-100' : 'bg-gray-900 text-gray-100'
                              }`}>
                                {JSON.stringify(testResponse, null, 2)}
                              </pre>
                            </div>
                          )}
                          
                          {/* Cross-partition verification for leaderboard queries */}
                          {!testError && testResponse && selectedEndpoint.id === 'content-fetch' && 
                           testQueryParams['contentType'] === 'leaderboard' && testResponse.success && testResponse.data && (
                            <div className="mt-4 bg-green-50 border-l-4 border-green-500 p-4 rounded">
                              <div className="text-sm text-green-800">
                                <p className="font-semibold mb-2">✅ Cross-Partition Query Verification</p>
                                {(() => {
                                  const contentItems = Array.isArray(testResponse.data) ? testResponse.data : [testResponse.data];
                                  const userIds = contentItems.map((item: any) => item.userId).filter(Boolean);
                                  const uniqueUserIds = [...new Set(userIds)];
                                  const ranks = contentItems.map((item: any) => item.rank_number).filter(Boolean);
                                  
                                  return (
                                    <>
                                      <div className="grid grid-cols-2 gap-4 mb-2">
                                        <div>
                                          <span className="font-medium">Total documents:</span> {contentItems.length}
                                        </div>
                                        <div>
                                          <span className="font-medium">Unique users:</span> {uniqueUserIds.length}
                                        </div>
                                      </div>
                                      {uniqueUserIds.length > 1 && (
                                        <div className="mt-2 p-2 bg-green-100 rounded">
                                          <p className="font-semibold text-green-900">🎉 Cross-Partition Success!</p>
                                          <p className="text-xs mt-1">
                                            Returned documents from {uniqueUserIds.length} different users: {uniqueUserIds.join(', ')}
                                          </p>
                                        </div>
                                      )}
                                      {ranks.length > 0 && (
                                        <div className="mt-2">
                                          <span className="font-medium">Ranks returned:</span> {ranks.sort((a: number, b: number) => a - b).join(', ')}
                                        </div>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Generated Image Preview */}
                        {!testError && testResponse && (() => {
                          // Check for /api/render response (image_base64)
                          if (testResponse.image_base64) {
                            return (
                              <div>
                                <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                  <span>🖼️</span>
                                  Generated Image
                                </h3>
                                <div className="bg-gray-50 rounded-lg p-6 flex flex-col items-center">
                                  <div className="bg-white rounded-lg shadow-lg p-4 max-w-md">
                                    <img
                                      src={`data:image/png;base64,${testResponse.image_base64}`}
                                      alt="Generated reward card"
                                      className="w-full h-auto rounded"
                                    />
                                  </div>
                                  <div className="mt-4 flex items-center gap-3">
                                    <a
                                      href={`data:image/png;base64,${testResponse.image_base64}`}
                                      download={`reward-card-${Date.now()}.png`}
                                      className="px-4 py-2 bg-linepe-orange text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors"
                                    >
                                      Download Image
                                    </a>
                                    <button
                                      onClick={() => {
                                        const img = new Image();
                                        img.src = `data:image/png;base64,${testResponse.image_base64}`;
                                        const w = window.open('');
                                        w?.document.write(img.outerHTML);
                                      }}
                                      className="px-4 py-2 bg-gray-600 text-white text-sm font-semibold rounded-lg hover:bg-gray-700 transition-colors"
                                    >
                                      Open in New Tab
                                    </button>
                                  </div>
                                  {testResponse.canvas && (
                                    <div className="mt-3 text-xs text-gray-600">
                                      Dimensions: {testResponse.canvas.w} × {testResponse.canvas.h}px
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          }
                          
                          // Check for /api/v1/content/fetch response (data.imageUrl or data[].imageUrl)
                          if (testResponse.success && testResponse.data) {
                            const contentItems = Array.isArray(testResponse.data) ? testResponse.data : [testResponse.data];
                            const itemsWithImages = contentItems.filter((item: any) => item && item.imageUrl);
                            
                            if (itemsWithImages.length > 0) {
                              // Helper function to get full image URL
                              const getFullImageUrl = (imageUrl: string) => {
                                // If it's already a full URL (starts with http:// or https://), return as-is
                                if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
                                  return imageUrl;
                                }
                                // Otherwise, prepend the base URL
                                const baseUrl = api['baseUrl'] || window.location.origin;
                                // Remove leading slash if present to avoid double slashes
                                const cleanPath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
                                return `${baseUrl}${cleanPath}`;
                              };
                              
                              return (
                                <div>
                                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                    <span>🖼️</span>
                                    {itemsWithImages.length === 1 ? 'Content Image' : `Content Images (${itemsWithImages.length})`}
                                  </h3>
                                  <div className="bg-gray-50 rounded-lg p-6 space-y-6">
                                    {itemsWithImages.map((item: any, index: number) => {
                                      const fullImageUrl = getFullImageUrl(item.imageUrl);
                                      return (
                                        <div key={item.id || index} className="flex flex-col items-center">
                                          <div className="bg-white rounded-lg shadow-lg p-4 max-w-md w-full">
                                            <img
                                              src={fullImageUrl}
                                              alt={`Content ${index + 1}`}
                                              className="w-full h-auto rounded"
                                              onError={(e) => {
                                                console.error('Image failed to load:', fullImageUrl);
                                                (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect width="400" height="300" fill="%23f0f0f0"/><text x="50%" y="50%" text-anchor="middle" fill="%23999">Image Load Error</text></svg>';
                                              }}
                                            />
                                          </div>
                                          {item.contentType && (
                                            <div className="mt-2 text-xs text-gray-600 flex items-center gap-2 flex-wrap justify-center">
                                              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{item.contentType}</span>
                                              {item.placement && <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{item.placement}</span>}
                                              {item.rank_number && <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded font-semibold">🏆 Rank #{item.rank_number}</span>}
                                              {item.userId && <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-mono text-xs">User: {item.userId}</span>}
                                            </div>
                                          )}
                                          <div className="mt-3 flex flex-col gap-2">
                                            <div className="flex items-center gap-3">
                                              <a
                                                href={fullImageUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="px-4 py-2 bg-linepe-orange text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors"
                                              >
                                                View Full Size
                                              </a>
                                              <button
                                                onClick={() => {
                                                  window.open(fullImageUrl, '_blank');
                                                }}
                                                className="px-4 py-2 bg-gray-600 text-white text-sm font-semibold rounded-lg hover:bg-gray-700 transition-colors"
                                              >
                                                Open in New Tab
                                              </button>
                                            </div>
                                            <div className="text-xs text-gray-500 text-center break-all px-2">
                                              {fullImageUrl}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            }
                          }
                          
                          return null;
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            Select an endpoint from the sidebar
          </div>
        )}
      </div>
    </div>
  );
}
